<template>
	<v-card class="my-profile-page page pt-5" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader v-if="profileSectionLoading" v-bind="attrs" type="card" />
			<v-card-text v-else-if="this.profile" v-for="(block, index) in myAccount" :key="index">
				<ShortProfileCard
					v-if="block.block_name === 'shortProfile'"
					:profile="block.block_data"
				/>
				<v-card
					v-if="block.block_name === 'myAccountButtons'"
					class="my-profile-page__button-section"
				>
					<button
						v-if="block.block_data.myBookmarks"
						@click="handleBlockLinkClick(block.block_data.myBookmarks.uri)"
						type="button"
						class="my-profile-page__button-section__button"
					>
						<Icon type="bookmark_empty" :size="24" color="var(--button-icon-tint)" />
						<span class="my-profile-page__button-section__button__label">{{
							block.block_data.myBookmarks.title || 'My Bookmarks'
						}}</span>
					</button>
					<button
						v-if="block.block_data.myNotes"
						@click="handleBlockLinkClick(block.block_data.myNotes.uri)"
						type="button"
						class="my-profile-page__button-section__button"
					>
						<Icon type="note" :size="24" color="var(--button-icon-tint)" />
						<span class="my-profile-page__button-section__button__label">{{
							block.block_data.myNotes.title || 'My Notes'
						}}</span>
					</button>
				</v-card>
				<v-card
					v-if="
						block.block_name === 'membership' &&
						(block.block_title ||
							block.block_data.renew_area_text ||
							block.block_data.renew_button_name)
					"
					class="my-profile-page__section"
				>
					<div class="d-flex flex-column">
						<span
							v-if="block.block_title"
							class="my-profile-page__section__block-name text-2-lines mb-2"
						>
							{{ block.block_title }}
						</span>
						<span
							v-if="block.block_data.renew_area_text"
							class="my-profile-page__section__renew-text text-2-lines"
						>
							{{ block.block_data.renew_area_text }}</span
						>
						<button
							v-if="block.block_data.renew_button_name || block.block_data.renew_url"
							@click="handleRenewButtonClick(block.block_data.renew_url)"
							type="button"
							class="my-profile-page__section__renew-button text-2-lines mt-3"
						>
							{{ block.block_data.renew_button_name }}
						</button>
					</div>
				</v-card>
				<v-card v-if="block.block_name === 'blockLinks'" class="my-profile-page__section">
					<div class="d-flex flex-column">
						<span class="my-profile-page__section__block-name text-2-lines mb-2">{{
							block.block_title
						}}</span>
						<div
							v-for="(link, index) in block.block_data.links"
							:key="index"
							class="my-profile-page__section__buttons-wrapper"
						>
							<button
								v-if="filterLink(link)"
								@click="handleBlockLinkClick(link.uri)"
								class="my-profile-page__section__buttons-wrapper__block-button mt-2"
							>
								<span
									class="my-profile-page__section__buttons-wrapper__block-button__label text-start text-2-lines"
								>
									{{ link.label }}
								</span>
								<Icon type="chevron_right" :size="14" color="#9b9b9b" />
							</button>
						</div>
					</div>
				</v-card>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PageBlocked from '@/components/PageBlocked'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'
import ShortProfileCard from '@/components/User/ShortProfileCard.vue'

export default {
	name: 'ProfilePage',
	mixins: [Block, Dwh],
	components: {
		ShortProfileCard,
		PageBlocked,
	},
	computed: {
		...mapGetters('auth', ['profile', 'isLoading', 'profileSection', 'profileSectionLoading']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),
		isVisibilityButton() {
			const visibilityList = this.profileSection?.visibilityInteraction || []
			const visibility =
				visibilityList.find((item) => item.block_name === 'visibilityInteraction') || {}
			return visibility?.block_data?.some((f) => f.field === 'show_user_list')
		},
		myAccount() {
			return this.profile.myAccount
		},
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert', 'setPageTitle']),
		...mapActions('auth', ['getProfileSection', 'clearProfileSection']),
		dwhViewMyAccount() {
			this.sendTrigger({
				category: 'Hub',
				action: 'view_my_account',
				module: 'My Account',
				title: 'My Account',
				id1: this.profile.user_id,
			})
		},
		dwhClickHandler(category, action, title, data) {
			this.sendTrigger({
				category: category,
				action: action,
				module: 'My Account',
				title: title,
				id1: this.profile.user_id,
				data: data,
			})
		},
		filterLink(item) {
			if (!item?.uri) return false

			const hiddenLinks = [
				'member-badge',
				'1to1meeting',
				'notifications-settings',
				'blocked-users',
			]

			if (!item.uri.startsWith('com.pinxterapp')) {
				return true
			}

			const path = item.uri.split('://')[1].split('?')[0]

			if (path === 'visibility-interactions') {
				return this.isVisibilityButton
			}

			return !hiddenLinks.includes(path)
		},
		handleRenewButtonClick(url) {
			this.dwhClickHandler('Membership', 'click_renew', 'Membership')
			if (url) {
				return window.open(url, '_blank')
			}
		},
		handleBlockLinkClick(uri) {
			if (!uri) return

			if (uri.startsWith('mailto:')) {
				this.dwhClickHandler('click_support_section', 'Account Support', uri)
				window.location.href = uri
				return
			}
			if (uri.startsWith('tel:')) {
				if (!this.isShowAlert) {
					const phone = uri.replace(/\D/g, '')
					this.dwhClickHandler('Hub', 'click_support_section', 'Account Support', phone)

					this.$copyText(phone).then(() => {
						const closeTimeout = setTimeout(() => {
							this.hideAlert('')
						}, 3000)

						this.showAlert({
							message: 'Phone number copied successfully.',
							closeTimeout,
						})
					})
				}
				return
			}
			if (uri.startsWith('http')) {
				this.dwhClickHandler('click_support_section', 'Account Support', uri)
				window.open(uri, '_self')
				return
			}
			if (uri.startsWith('com.pinxterapp')) {
				const path = uri.split('://')[1]
				const dwhMap = {
					'personal-information': {
						action: 'click_personal_information',
						title: 'Personal Information',
					},
					'my-bookmarks': {
						action: 'click_bookmarks_tile',
						title: 'Bookmarks',
					},
					'my-notes': {
						action: 'click_notes_tile',
						title: 'My Notes',
					},
					'visibility-interactions': {
						action: 'click_privacy_section',
						title: 'Privacy & Visibility',
					},
				}
				const dwhData = dwhMap[path]
				if (dwhData) {
					this.dwhClickHandler('Hub', dwhData.action, dwhData.title)
				} else {
					this.dwhClickHandler('click_support_section', 'Account Support', uri)
				}
				this.$router.push(path)
				return
			}
		},
	},
	data() {
		return {
			attrs: {
				elevation: 0,
			},
			socialFieldNames: ['user_linkedin', 'user_facebook', 'user_twitter', 'user_custom_url'],
			phoneFieldNames: ['user_work_phone', 'user_cell_phone'],
			pageBlockedName: 'profile',
		}
	},
	mounted() {
		this.dwhViewMyAccount()
		this.getProfileSection('visibilityInteraction')
		this.setPageTitle('My Account')
	},
	beforeDestroy() {
		this.clearProfileSection()
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.my-profile-page {
	background-color: transparent !important;
	&__button-section {
		max-width: 555px;
		margin-bottom: 8px;
		background-color: unset !important;
		box-shadow: none !important;
		display: flex !important;
		gap: 8px;
		&__button {
			width: 100%;
			height: 75px;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 8px;
			border: 1px solid #e2e2e2;
			border-radius: 8px;
			background-color: #ffffff;
			&__label {
				font-family: Inter, sans-serif;
				font-weight: 500;
				font-size: 16px;
				line-height: 100%;
				letter-spacing: 0;
				color: var(--username-category-title);
			}
		}
	}
	&__section {
		max-width: 555px;
		border-radius: 8px !important;
		margin-bottom: 8px;
		border: 1px solid #e2e2e2 !important;
		padding: 12px;
		background-color: #ffffff !important;
		box-shadow: none !important;
		&__buttons-wrapper {
			display: flex;
			flex-direction: column;
			gap: 8px;
			&__block-button {
				width: 100%;
				height: 48px;
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 12px;
				background-color: #f3f3f6;
				border-radius: 8px;
				&__label {
					font-family: Inter, sans-serif;
					font-weight: 500;
					font-size: 16px;
					line-height: 100%;
					letter-spacing: 0;
					color: #4a4a4a;
				}
			}
		}
		&__block-name {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
		&__renew-text {
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			letter-spacing: 0;
			color: #9b9b9b;
		}
		&__renew-button {
			width: 100%;
			height: 46px;
			border: 1.5px solid var(--secondary_color);
			border-radius: 8px;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--secondary_color);
		}

		&-title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
			margin-bottom: 12px;
		}

		&-label {
			font-family: 'Inter', sans-serif;
			font-weight: 600;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
			margin-right: 8px;
		}

		&-text {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}
}
.text-2-lines {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	word-break: break-word;
}
</style>
